import { Component, OnDestroy, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { GlobalService } from 'src/app/core/services/global.service';
import { MasterService } from 'src/app/core/services/master.service';

export interface PeriodicElement {
  _id?: string;
  icon?: string;
  preferenceTypeId?: string;
  preferenceOptionType?: string;
  PreferenceSelectionValue?: string;
  title?: Array<{ Lang: string; Value: string; _id?: string }>;
  description?: Array<{ Lang: string; Value: string; _id?: string }>;
  preferenceOptions?: Array<{ Lang: string; Value: string; _id?: string; SelectedValue?: boolean }>;
}

interface PreferenceOptionPayload {
  Lang: string;
  Value: string;
  _id?: string;
  SelectedValue: boolean;
}

interface PreferencePayload {
  id: string;
  Title: string;
  Description: string;
  PreferenceTypeId: string;
  PreferenceOptionType: string;
  PreferenceOptions: PreferenceOptionPayload[];
  PreferenceToggleValue: boolean;
  PreferenceSelectionValue: string;
  PreferenceSelectionArray: string[];
}

interface EnabledPreferenceMeta {
  id: string;
  title: string;
  type: string;
}


@Component({
  selector: 'app-preferences',
  templateUrl: './preferences.component.html',
  styleUrls: ['./preferences.component.scss']
})
export class PreferencesComponent implements OnInit, OnDestroy {
  preferenceTypeIds: string[] = [
    '5fe7391a2b03e2067e0879b3',
    '5cfa6365d43cc23fe0829e7f',
    '5fe739242b03e2067e0879b4'
  ];
  currentPreferenceTypeId = this.preferenceTypeIds[0];
  length = 0;
  pageSize = 10;
  pageSizeOptions: number[] = [5, 10, 25, 100];
  pageEvent: PageEvent;
  displayedColumns: string[] = ['name', 'action'];
  dataSource: PeriodicElement[] = [];
  selectedPreferences: PreferencePayload[] = [];
  enabledPreferences: { [id: string]: EnabledPreferenceMeta } = {};
  validationErrors: { [id: string]: string } = {};

  constructor(
    public globalService: GlobalService,
    private masterService: MasterService,
    private router: Router
  ) {}

  ngOnInit() {
    this.showAllPreferences(this.currentPreferenceTypeId);
  }

  getPaginatorData(event: any) {
    this.pageSize = event.pageSize;
  }

  onPreferenceTabChange(event: MatTabChangeEvent) {
    this.currentPreferenceTypeId = this.preferenceTypeIds[event.index] || this.preferenceTypeIds[0];
    this.showAllPreferences(this.currentPreferenceTypeId);
  }

  showAllPreferences(preferenceType: string) {
    this.globalService.showLoader = true;
    this.masterService
      .showAllPreferences({
        LangSave: this.globalService.getLangSaveProperty(),
        preferenceType
      })
      .pipe(takeUntil(this.globalService.componentDestroyed(this)))
      .subscribe(
        response => {
          this.globalService.showLoader = false;
          if (response && response.Status) {
            const preferences = Array.isArray(response.Data) ? response.Data : [];
            this.dataSource = preferences;
            this.length = this.dataSource.length;
            return;
          }
          this.globalService.handleApiError(response);
        },
        error => {
          this.globalService.showLoader = false;
          this.globalService.handleApiError(error);
        }
      );
  }

  getLocalizedValue(list: Array<{ Lang: string; Value: string }>): string {
    if (!Array.isArray(list) || !list.length) {
      return '';
    }
    const currentLang = this.globalService.getLangSaveProperty();
    const selected = list.find(item => item.Lang === currentLang);
    if (selected && selected.Value) {
      return selected.Value;
    }
    return list[0].Value || '';
  }

  isChoiceType(type: string): boolean {
    return type === 'radio' || type === 'checkbox' || type === 'dropdown';
  }

  isCheckboxType(type: string): boolean {
    return type === 'checkbox';
  }

  isRadioType(type: string): boolean {
    return type === 'radio';
  }

  isDropdownType(type: string): boolean {
    return type === 'dropdown';
  }

  isTextType(type: string): boolean {
    return type === 'text';
  }

  isCheckboxChecked(option: any): boolean {
    return !!option?.SelectedValue;
  }

  isPreferenceEnabled(preference: any): boolean {
    return !!this.enabledPreferences[preference?._id];
  }

  onPreferenceToggle(preference: any, checked: boolean) {
    if (!preference?._id) {
      return;
    }

    const preferenceId = preference._id;

    if (checked) {
      this.enabledPreferences[preferenceId] = {
        id: preferenceId,
        title: this.getLocalizedValue(preference.title || []),
        type: preference.preferenceOptionType || ''
      };
      this.updatePreferenceValidation(preference);
      return;
    }

    delete this.enabledPreferences[preferenceId];
    delete this.validationErrors[preferenceId];
    this.selectedPreferences = this.selectedPreferences.filter((item: PreferencePayload) => item.id !== preferenceId);

    if (Array.isArray(preference.preferenceOptions)) {
      preference.preferenceOptions = preference.preferenceOptions.map((item: any) => ({
        ...item,
        SelectedValue: false
      }));
    }
  }

  onCheckboxToggle(preference: any, option: any, checked: boolean) {
    if (!preference || !option) {
      return;
    }

    option.SelectedValue = checked;

    const selectedPreference = this.ensureSelectedPreference(preference);
    const allOptions = this.mapPreferenceOptions(preference.preferenceOptions || []).map((item: PreferenceOptionPayload) => ({
      ...item,
      SelectedValue: item._id === option._id ? checked : !!item.SelectedValue
    }));
    selectedPreference.PreferenceOptions = allOptions.filter((item: PreferenceOptionPayload) => !!item.SelectedValue);

    const hasAnySelected = selectedPreference.PreferenceOptions.length > 0;
    selectedPreference.PreferenceToggleValue = hasAnySelected;
    selectedPreference.PreferenceSelectionValue = '';
    selectedPreference.PreferenceSelectionArray = [];

    if (!hasAnySelected) {
      this.selectedPreferences = this.selectedPreferences.filter((item: PreferencePayload) => item.id !== preference._id);
    }

    this.updatePreferenceValidation(preference);
  }

  onRadioSelect(preference: any, selectedOptionId: string) {
    if (!preference || !selectedOptionId) {
      return;
    }

    const option = (preference.preferenceOptions || []).find((item: any) => item._id === selectedOptionId);
    if (!option) {
      return;
    }

    const selectedPreference = this.ensureSelectedPreference(preference);
    const selected = this.mapPreferenceOptions(preference.preferenceOptions || []).find(
      (item: PreferenceOptionPayload) => item._id === selectedOptionId
    );
    selectedPreference.PreferenceOptions = selected
      ? [{ ...selected, SelectedValue: true }]
      : [];
    selectedPreference.PreferenceSelectionValue = this.getLocalizedValue([option]);
    selectedPreference.PreferenceSelectionArray = [];
    selectedPreference.PreferenceToggleValue = true;
    this.updatePreferenceValidation(preference);
  }

  onDropdownSelect(preference: any, selectedOptionId: string) {
    if (!preference || !selectedOptionId) {
      return;
    }

    const option = (preference.preferenceOptions || []).find((item: any) => item._id === selectedOptionId);
    if (!option) {
      return;
    }

    const selectedPreference = this.ensureSelectedPreference(preference);
    const selected = this.mapPreferenceOptions(preference.preferenceOptions || []).find(
      (item: PreferenceOptionPayload) => item._id === selectedOptionId
    );
    selectedPreference.PreferenceOptions = selected
      ? [{ ...selected, SelectedValue: true }]
      : [];
    selectedPreference.PreferenceSelectionValue = this.getLocalizedValue([option]);
    selectedPreference.PreferenceSelectionArray = [];
    selectedPreference.PreferenceToggleValue = true;
    this.updatePreferenceValidation(preference);
  }

  onTextValueChange(preference: any, value: string) {
    if (!preference) {
      return;
    }

    const textValue = (value || '').trim();
    if (!textValue) {
      this.selectedPreferences = this.selectedPreferences.filter((item: PreferencePayload) => item.id !== preference._id);
      this.updatePreferenceValidation(preference);
      return;
    }

    const selectedPreference = this.ensureSelectedPreference(preference);
    selectedPreference.PreferenceSelectionValue = textValue;
    selectedPreference.PreferenceOptions = [];
    selectedPreference.PreferenceSelectionArray = [];
    selectedPreference.PreferenceToggleValue = true;
    this.updatePreferenceValidation(preference);
  }

  getSelectedOptionId(preference: any): string {
    const selectedPreference = this.selectedPreferences.find((item: PreferencePayload) => item.id === preference?._id);
    if (selectedPreference?.PreferenceOptions?.length) {
      const selected = selectedPreference.PreferenceOptions.find((item: PreferenceOptionPayload) => !!item.SelectedValue);
      return selected?._id || '';
    }
    return '';
  }

  getSelectedTextValue(preference: any): string {
    const selectedPreference = this.selectedPreferences.find((item: PreferencePayload) => item.id === preference?._id);
    return selectedPreference?.PreferenceSelectionValue || '';
  }

  goToSelectedPreferences() {
    if (!this.validateEnabledPreferences()) {
      this.globalService.handleErrorMessage('Please complete all enabled preferences');
      return;
    }

    sessionStorage.setItem('selectedPreferences', JSON.stringify(this.selectedPreferences || []));
    this.router.navigate(['/selected-preferences']);
  }

  private validateEnabledPreferences(): boolean {
    const enabledPreferenceIds = Object.keys(this.enabledPreferences || {});
    if (!enabledPreferenceIds.length) {
      return true;
    }

    let isAllValid = true;
    enabledPreferenceIds.forEach((preferenceId: string) => {
      const preferenceMeta = this.enabledPreferences[preferenceId];
      const selectedPreference = this.selectedPreferences.find((item: PreferencePayload) => item.id === preferenceId);
      const isValid = this.isPreferenceSelectionValid(preferenceMeta?.type || '', selectedPreference);

      if (!isValid) {
        this.validationErrors[preferenceId] = this.getValidationErrorMessage(preferenceMeta?.type || '');
        isAllValid = false;
      } else {
        delete this.validationErrors[preferenceId];
      }
    });

    return isAllValid;
  }

  private updatePreferenceValidation(preference: any) {
    const preferenceId = preference?._id;
    if (!preferenceId || !this.enabledPreferences[preferenceId]) {
      return;
    }

    const selectedPreference = this.selectedPreferences.find((item: PreferencePayload) => item.id === preferenceId);
    const isValid = this.isPreferenceSelectionValid(preference.preferenceOptionType || '', selectedPreference);

    if (isValid) {
      delete this.validationErrors[preferenceId];
      return;
    }

    this.validationErrors[preferenceId] = this.getValidationErrorMessage(preference.preferenceOptionType || '');
  }

  private getValidationErrorMessage(type: string): string {
    switch (type) {
      case 'checkbox':
        return 'Please select at least one option';
      case 'radio':
      case 'dropdown':
        return 'Please select an option';
      case 'text':
        return 'Please enter a value';
      default:
        return 'This field is required';
    }
  }

  private isPreferenceSelectionValid(type: string, selectedPreference?: PreferencePayload): boolean {
    switch (type) {
      case 'checkbox':
        return !!selectedPreference?.PreferenceOptions?.some((item: PreferenceOptionPayload) => !!item.SelectedValue);
      case 'radio':
      case 'dropdown':
        return !!selectedPreference?.PreferenceOptions?.length;
      case 'text':
        return !!(selectedPreference?.PreferenceSelectionValue || '').trim();
      default:
        return true;
    }
  }

  private ensureSelectedPreference(preference: any) {
    let selectedPreference = this.selectedPreferences.find((item: PreferencePayload) => item.id === preference._id);
    if (!selectedPreference) {
      selectedPreference = {
        id: preference._id,
        Title: this.getLocalizedValue(preference.title || []),
        Description: this.getLocalizedValue(preference.description || []),
        PreferenceTypeId: preference.preferenceTypeId,
        PreferenceOptionType: preference.preferenceOptionType,
        PreferenceOptions: this.mapPreferenceOptions(preference.preferenceOptions || []),
        PreferenceToggleValue: false,
        PreferenceSelectionValue: '',
        PreferenceSelectionArray: []
      };
      this.selectedPreferences.push(selectedPreference);
    } else {
      selectedPreference.Title = this.getLocalizedValue(preference.title || []);
      selectedPreference.Description = this.getLocalizedValue(preference.description || []);
      selectedPreference.PreferenceTypeId = preference.preferenceTypeId;
      selectedPreference.PreferenceOptionType = preference.preferenceOptionType;
      if (!Array.isArray(selectedPreference.PreferenceOptions)) {
        selectedPreference.PreferenceOptions = [];
      }
    }
    return selectedPreference;
  }

  private mapPreferenceOptions(options: any[]): PreferenceOptionPayload[] {
    if (!Array.isArray(options)) {
      return [];
    }
    return options.map((option: any) => ({
      Lang: option?.Lang || this.globalService.getLangSaveProperty(),
      Value: option?.Value || '',
      _id: option?._id,
      SelectedValue: !!option?.SelectedValue
    }));
  }

  ngOnDestroy() {}
}
